/** @format */

import {useCallback, useRef, useState} from 'react';

import {ColumnSizingState} from '@tanstack/react-table';

import {isObject, safeParseJson} from 'helpers/miscHelpers';

import {MIN_COLUMN_WIDTH} from '../constants';

export const COLUMN_SIZING_STORAGE_PREFIX = 'table-column-sizing:';

// Id колонок, совпадающие со служебными свойствами объекта, в columnSizing не попадают: реальной
// колонки с таким id быть не может, а в объекте они ведут себя не как обычные ключи
// (`acc.constructor = 200` затирает ссылку на конструктор, `acc.__proto__ = 200` молча
// игнорируется). Проще отбросить на входе, чем разбираться в последствиях ниже по течению.
const UNSAFE_COLUMN_IDS = ['__proto__', 'constructor', 'prototype'];

interface Params {
  // Ключ таблицы; не задан — фича выключена, хранилище не читается и не пишется.
  key?: string;
}

interface ColumnSizingStorage {
  storedSizing: ColumnSizingState;
  persistSizing: (sizing: ColumnSizingState) => void;
  // Перечитывает хранилище под ТЕКУЩИМ key прямо сейчас — используется при смене
  // columnResizingKey в рантайме (см. useTableModel). Не «просто чтение»: попутно
  // переустанавливает базу для дифф-удаления в persistSizing (см. previousSizingRef ниже) —
  // поэтому имя отражает эффект «принять» новое хранилище как текущее, а не только прочитать его.
  adoptStoredSizing: () => ColumnSizingState;
}

const parseStoredSizing = (raw: string): ColumnSizingState => {
  // safeParseJson берёт на себя и битый JSON, и проверку формы: isObject отсекает не-объекты и
  // массивы (у массива числовые ключи прошли бы фильтр ниже, будь в таблице колонка с id «0»).
  const parsed = safeParseJson(raw, isObject);
  if (!parsed) {
    return {};
  }

  return Object.entries(parsed).reduce<ColumnSizingState>((acc, [columnId, width]) => {
    const isValidWidth =
      typeof width === 'number' && Number.isFinite(width) && width >= MIN_COLUMN_WIDTH;
    if (isValidWidth && !UNSAFE_COLUMN_IDS.includes(columnId)) {
      acc[columnId] = width;
    }
    return acc;
  }, {});
};

const readStoredSizing = (key: string): ColumnSizingState => {
  try {
    const raw = localStorage.getItem(`${COLUMN_SIZING_STORAGE_PREFIX}${key}`);
    return raw ? parseStoredSizing(raw) : {};
  } catch {
    // Битый JSON или недоступное хранилище — работаем с ширинами в памяти.
    return {};
  }
};

export const useColumnSizingStorage = ({key}: Params): ColumnSizingStorage => {
  const [storedSizing] = useState<ColumnSizingState>(() => (key ? readStoredSizing(key) : {}));

  // Полный сайзинг, последний раз известный ЭТОМУ инстансу хука (этой вкладке/инстансу таблицы).
  // Нужен, чтобы persistSizing мог отличить «пользователь в этой вкладке убрал колонку из sizing»
  // (её нужно удалить и в сторадже) от «эта колонка просто никогда не была известна этой вкладке»
  // (её мог добавить другой таб/инстанс — трогать нельзя). Обновляется и persistSizing, и
  // adoptStoredSizing.
  const previousSizingRef = useRef<ColumnSizingState>(storedSizing);

  const persistSizing = useCallback(
    (sizing: ColumnSizingState) => {
      if (!key) {
        return;
      }
      const previous = previousSizingRef.current;
      previousSizingRef.current = sizing;
      try {
        // Read-modify-write поверх АКТУАЛЬНОГО содержимого ключа (а не поверх снимка,
        // прочитанного при монтировании) — иначе конкурентная запись из другого таба/инстанса
        // теряется целиком (см. дефект #5).
        const current = readStoredSizing(key);
        const merged = {...current};
        // Колонки, которые пропали из sizing ИМЕННО в этом инстансе (previous → sizing), удаляем
        // и из смёрженного результата — иначе resetColumnSize перестанет что-либо удалять из
        // хранилища, если другой таб успел записать что-то параллельно.
        Object.keys(previous).forEach(columnId => {
          if (!(columnId in sizing)) {
            delete merged[columnId];
          }
        });
        Object.assign(merged, sizing);
        localStorage.setItem(`${COLUMN_SIZING_STORAGE_PREFIX}${key}`, JSON.stringify(merged));
      } catch {
        // Недоступное хранилище — молча деградируем.
      }
    },
    [key],
  );

  // Стабильна между рендерами, пока не меняется key: если бы adoptStoredSizing была новой функцией
  // на каждый рендер, эффект в useTableModel, который на неё подписан, перезапускался бы на каждый
  // ре-рендер и откатывал бы уже изменённую пользователем ширину обратно к значению из localStorage.
  const adoptStoredSizing = useCallback((): ColumnSizingState => {
    const next = key ? readStoredSizing(key) : {};
    previousSizingRef.current = next;
    return next;
  }, [key]);

  return {storedSizing, persistSizing, adoptStoredSizing};
};
